import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CrewMember } from "@/mocks/admin/crew";

type ShiftRow = {
  id: string;
  crew_id: string;
  project_id: string;
  clock_in: string;
  clock_out: string | null;
  total_hours: number | null;
  status: string;
  hourly_rate: number;
  shift_date: string;
  flag: string | null;
};

type Project = {
  id: string;
  customer_name: string;
  move_type: string;
  status: string;
  total: number;
};

type Props = { member: CrewMember };

export default function PayTab({ member }: Props) {
  const [shifts, setShifts] = useState<ShiftRow[]>([]);
  const [projects, setProjects] = useState<Record<string, Project>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data: shiftRows } = await supabase
        .from("shifts")
        .select("*")
        .eq("crew_id", member.id);

      // also pull a few recent project rows to enrich payouts
      const { data: projectRows } = await supabase
        .from("moving_projects")
        .select("id, customer_name, move_type, status, total");

      if (cancelled) return;
      setShifts((shiftRows as ShiftRow[]) || []);
      const map: Record<string, Project> = {};
      ((projectRows as Project[]) || []).forEach((p) => {
        map[p.id] = p;
      });
      setProjects(map);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [member.id]);

  // Build "payouts per project" — group by project_id, sum hours * rate
  const payouts = useMemo(() => {
    const grouped: Record<
      string,
      { projectId: string; hours: number; pay: number; shifts: number; status: string }
    > = {};
    shifts.forEach((s) => {
      const h = s.total_hours || 0;
      if (!grouped[s.project_id]) {
        grouped[s.project_id] = {
          projectId: s.project_id,
          hours: 0,
          pay: 0,
          shifts: 0,
          status: s.status,
        };
      }
      grouped[s.project_id].hours += h;
      grouped[s.project_id].pay += h * (s.hourly_rate || member.hourlyRate);
      grouped[s.project_id].shifts += 1;
      // upgrade status priority: completed > pending > approved
      if (s.status === "completed") grouped[s.project_id].status = "paid";
      else if (s.status === "pending-approval" && grouped[s.project_id].status !== "paid")
        grouped[s.project_id].status = "pending";
    });
    return Object.values(grouped)
      .filter((p) => p.hours > 0)
      .sort((a, b) => b.pay - a.pay);
  }, [shifts, member.hourlyRate]);

  // weekly totals (this week vs last)
  const totalLoggedHours = shifts.reduce((sum, s) => sum + (s.total_hours || 0), 0);
  const totalPay = shifts.reduce(
    (sum, s) => sum + (s.total_hours || 0) * (s.hourly_rate || member.hourlyRate),
    0,
  );
  const thisWeekPay = member.weeklyHours * member.hourlyRate;
  const otHours = Math.max(0, member.weeklyHours - 40);
  const regHours = member.weeklyHours - otHours;

  // Demo: show "next payout" as Friday
  const today = new Date();
  const nextFriday = new Date(today);
  nextFriday.setDate(today.getDate() + ((5 + 7 - today.getDay()) % 7 || 7));
  const payDate = nextFriday.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="px-5 py-5 space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold text-ink-900">Earnings</h2>
        <p className="text-[11px] text-ink-500 mt-0.5">Your payouts and timesheet totals.</p>
      </div>

      {/* Next payout hero */}
      <section className="rounded-2xl p-5 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10 blur-3xl"></div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/80">
            Next Payout
          </div>
          <div className="flex items-end gap-2 mt-1">
            <div className="font-display text-4xl font-bold tabular-nums leading-none">
              ${thisWeekPay.toFixed(0)}
            </div>
            <div className="text-xs text-white/70 pb-1">.{((thisWeekPay % 1) * 100).toFixed(0).padStart(2, "0")}</div>
          </div>
          <div className="text-[11px] text-white/80 mt-1.5">
            Deposits {payDate} · direct to checking ••3429
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/15">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">
                Regular
              </div>
              <div className="font-display text-base font-bold tabular-nums mt-0.5">{regHours.toFixed(1)}h</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">OT</div>
              <div className="font-display text-base font-bold tabular-nums mt-0.5">{otHours.toFixed(1)}h</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">Rate</div>
              <div className="font-display text-base font-bold tabular-nums mt-0.5">${member.hourlyRate}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent payouts per project */}
      <section>
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="font-display text-base font-bold text-ink-900">Payouts by Job</h3>
          <span className="text-[10px] text-ink-500 font-semibold">
            {payouts.length} {payouts.length === 1 ? "job" : "jobs"}
          </span>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-ink-500">
            <i className="ri-loader-4-line animate-spin mr-1.5"></i>
            Loading…
          </div>
        ) : payouts.length === 0 ? (
          <div className="bg-white border border-dashed border-ink-200 rounded-2xl p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-cream-100 text-ink-400 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl"></i>
            </div>
            <div className="text-sm font-bold text-ink-900 mt-2">No payouts yet</div>
            <p className="text-[11px] text-ink-500 mt-1">
              Clock in on a job and earnings will show here.
            </p>
          </div>
        ) : (
          <ul className="space-y-2.5">
            {payouts.map((p) => {
              const project = projects[p.projectId];
              return (
                <li key={p.projectId} className="bg-white border border-ink-100 rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] font-bold text-brand-700 bg-brand-100 px-2 py-0.5 rounded-full">
                          {p.projectId}
                        </span>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            p.status === "paid"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {p.status === "paid" ? "Paid" : "Pending"}
                        </span>
                      </div>
                      <div className="text-sm font-bold text-ink-900 truncate">
                        {project?.customer_name || "Unassigned project"}
                      </div>
                      <div className="text-[11px] text-ink-500 truncate">
                        {project?.move_type || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-lg font-bold text-ink-900 tabular-nums">
                        ${p.pay.toFixed(0)}
                      </div>
                      <div className="text-[10px] text-ink-500">
                        {p.hours.toFixed(1)}h · {p.shifts} shift{p.shifts > 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Lifetime totals */}
      <section className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-ink-100 rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
            Hours Logged
          </div>
          <div className="font-display text-2xl font-bold text-ink-900 mt-1 tabular-nums">
            {totalLoggedHours.toFixed(1)}h
          </div>
          <div className="text-[10px] text-ink-500 mt-0.5">All-time tracked</div>
        </div>
        <div className="bg-white border border-ink-100 rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
            Earnings (All)
          </div>
          <div className="font-display text-2xl font-bold text-emerald-600 mt-1 tabular-nums">
            ${totalPay.toFixed(0)}
          </div>
          <div className="text-[10px] text-ink-500 mt-0.5">Across {payouts.length} jobs</div>
        </div>
      </section>

      <section className="bg-cream-100 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-white text-ink-700 flex items-center justify-center shrink-0">
            <i className="ri-question-line text-base"></i>
          </span>
          <div>
            <div className="text-sm font-bold text-ink-900">Question about pay?</div>
            <p className="text-[11px] text-ink-500 mt-0.5 leading-relaxed">
              Reach out to payroll at payroll@haulmark.co or text dispatch. We respond within 24h.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}